import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArticleCard } from "@/components/public/ArticleCard";
import { NewsletterBlock } from "@/components/public/NewsletterBlock";
import { catLabel } from "@/lib/format";
import { useSeo, organizationJsonLd } from "@/lib/seo";

function SectionHeader({ title, to }) {
  return (
    <div className="flex items-end justify-between mb-5 border-b-2 border-ink pb-2">
      <h2 className="font-serif font-bold text-2xl sm:text-3xl text-ink">{title}</h2>
      {to && <Link to={to} className="text-xs font-semibold uppercase tracking-widest text-crimson hover:text-crimsonhover">View all →</Link>}
    </div>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/homepage").then((r) => setData(r.data)).catch(() => {}); }, []);
  useSeo({
    description: "The essential developments across fintech, payments, banking and financial infrastructure. Independent reporting with a European focus.",
    jsonLd: [
      organizationJsonLd(),
      { "@type": "WebSite", name: "The Fin Desk", url: "https://thefindesk.com",
        potentialAction: { "@type": "SearchAction", target: "https://thefindesk.com/search?q={q}", "query-input": "required name=q" } },
    ],
  });
  if (!data) return <div className="max-w-[1280px] mx-auto px-6 py-20 text-mutedink">Loading the desk…</div>;

  const { lead, secondary, sections, most_read, editors_picks, insights, latest } = data;

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 py-8">
      {/* Hero newsroom */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-7">{lead && <ArticleCard a={lead} variant="lead" />}</div>
        <div className="lg:col-span-5 lg:border-l lg:border-hair lg:pl-10">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-crimson mb-2">Top Stories</div>
          {secondary?.map((a) => <ArticleCard key={a.id} a={a} variant="secondary" />)}
        </div>
      </div>

      {/* Category grid: payments / fintech / banking */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
        {["payments", "fintech", "banking"].map((cat) => (
          <div key={cat} className="md:border-l md:first:border-0 md:pl-8 md:first:pl-0">
            <SectionHeader title={catLabel(cat)} to={`/${cat}`} />
            {(sections[cat] || []).slice(0, 1).map((a) => <ArticleCard key={a.id} a={a} variant="standard" />)}
            {(sections[cat] || []).slice(1, 4).map((a) => <ArticleCard key={a.id} a={a} variant="compact" />)}
            {!(sections[cat] || []).length && <p className="text-sm text-mutedink">Coverage forthcoming.</p>}
          </div>
        ))}
      </div>

      {/* Analysis spotlight (dark) */}
      <div className="bg-navy text-paper mt-16 p-8 sm:p-12">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold mb-4">Analysis & Insights</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {(insights || []).slice(0, 3).map((a) => (
            <Link key={a.id} to={`/${a.category}/${a.slug}`} className="group">
              <div className="text-[10px] uppercase tracking-widest text-gold">{a.content_type}</div>
              <h3 className="font-serif text-xl sm:text-2xl mt-2 leading-snug group-hover:text-gold transition-colors">{a.headline}</h3>
              <p className="mt-2 text-sm text-paper/60 line-clamp-3">{a.subtitle}</p>
            </Link>
          ))}
          {!(insights || []).length && <p className="text-paper/60 text-sm">Analysis forthcoming.</p>}
        </div>
      </div>

      {/* Infrastructure + Most Read + Editor's Picks */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16">
        <div className="lg:col-span-8">
          <SectionHeader title="Financial Infrastructure" to="/infrastructure" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {(sections.infrastructure || []).slice(0, 4).map((a) => <ArticleCard key={a.id} a={a} variant="standard" />)}
            {!(sections.infrastructure || []).length && <p className="text-sm text-mutedink">Coverage forthcoming.</p>}
          </div>
        </div>
        <div className="lg:col-span-4 lg:border-l lg:border-hair lg:pl-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-crimson mb-3 border-b-2 border-ink pb-2">Most Read</div>
          {(most_read || []).map((a, i) => <ArticleCard key={a.id} a={a} variant="numbered" index={i + 1} />)}
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-crimson mb-3 mt-10 border-b-2 border-ink pb-2">Editor's Picks</div>
          {(editors_picks || []).map((a) => <ArticleCard key={a.id} a={a} variant="compact" />)}
        </div>
      </div>

      {/* Digital assets + regulation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-16">
        {["digital-assets", "regulation"].map((cat) => (
          <div key={cat}>
            <SectionHeader title={catLabel(cat)} to={`/${cat}`} />
            <div className="grid grid-cols-2 gap-6">
              {(sections[cat] || []).slice(0, 2).map((a) => <ArticleCard key={a.id} a={a} variant="standard" />)}
              {!(sections[cat] || []).length && <p className="text-sm text-mutedink col-span-2">Coverage forthcoming.</p>}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-16"><NewsletterBlock /></div>
    </div>
  );
}
